import { createAsyncThunk } from '@reduxjs/toolkit'
import mainTranslate from 'loankeeper-common/helpers/language'
import { OpDayViewServerData } from '@lk/api'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { loadLivedata } from '../livedata'
import { ThunkApi } from '../index.d'
import { kickAllUser } from '../system'
import { toDateString, getDateDiff, getDateInstance } from './opday.helpers'
import { getOpDay } from './opday.selectors'

const translate = (key) => mainTranslate(`LKMESSAGE-${key}`)

export type LoadOpDayPayload = OpDayViewServerData & {
  DirtyDays: number
  AccDays: number
  CreditDays: number
}

export const loadOpDay = createAsyncThunk<LoadOpDayPayload, void, ThunkApi>(
  '[OP_DAY] LOAD_OP_DAY',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.opday.get()

    if (result.success) {
      const { Open_Day, DirtyDate, AccDate, CreditDate, Cur_Date, BlockedDate } = result.data

      return {
        ...result.data,
        Open_Day,
        DirtyDate: toDateString(DirtyDate),
        AccDate: toDateString(AccDate),
        CreditDate: toDateString(CreditDate),
        Cur_Date: toDateString(Cur_Date),
        BlockedDate: toDateString(BlockedDate),
        DirtyDays: getDateDiff(AccDate, DirtyDate, 'days'),
        AccDays: getDateDiff(CreditDate, AccDate, 'days'),
        CreditDays: getDateDiff(Cur_Date, CreditDate, 'days'),
      }
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))

    return rejectWithValue('Failed to load opDay data')
  },
)

export const blockOpDay = createAsyncThunk<any, void, ThunkApi>(
  '[OP_DAY] BLOCK_OP_DAY',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const kickAllUserResult = await dispatch(kickAllUser())

    if (kickAllUser.rejected.match(kickAllUserResult as any)) {
      return rejectWithValue('Failed to kick all users')
    }

    const result = await extra.api.opday.block()

    if (result.success) {
      dispatch(loadLivedata())
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))

    return rejectWithValue('Failed to block opDay')
  },
)

export const unblockOpDay = createAsyncThunk<any, void, ThunkApi>(
  '[OP_DAY] UNBLOCK_OP_DAY',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.opday.unblock()

    if (result.success) {
      dispatch(loadLivedata())
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))

    return rejectWithValue('Failed to unblock opDay')
  },
)

export interface ProceedToDayAttributes {
  accountOnly: boolean
  goToDate: string
  onValidate: (validationResult: any) => void
  onProgress: (currentDay: any, totalDays: number) => void
  singleRun?: boolean
  needValidation?: boolean
  action?: 'open' | 'close' | 'openAccOnly' | 'closeAccOnly'
}

const getSinglePerformOptions = ({ action, extra }) => {
  let performed = false

  return {
    perform: () => {
      if (action === 'open') {
        return extra.api.opday.open()
      }

      return extra.api.opday.close()
    },
    shouldPerform: () => {
      const should = !performed

      if (should) {
        performed = true
      }

      return should
    },
    onSuccess: () => {},
  }
}

const getPerformOptions = ({ openDay, goToDate, extra, accountOnly, onProgress }) => {
  const goToDay = getDateInstance(goToDate)
  const isOpenAction = goToDay.isBefore(openDay)
  const totalDays = isOpenAction ? openDay.diff(goToDay, 'days') : goToDay.diff(openDay, 'days')

  return {
    perform: () => {
      if (isOpenAction) {
        return accountOnly ? extra.api.opday.openAccountOnly() : extra.api.opday.open()
      }
      return accountOnly ? extra.api.opday.closeAccountOnly() : extra.api.opday.close()
    },
    shouldPerform: ({ currentDay }) => {
      return !currentDay.isSame(goToDay)
    },
    onSuccess: ({ currentDay }) => {
      onProgress(currentDay, totalDays)
    },
  }
}

export const proceedToDay = createAsyncThunk<void, ProceedToDayAttributes, ThunkApi>(
  '[OP_DAY] PROCEED_TO_DAY',
  async (
    { accountOnly, goToDate, needValidation, onValidate, onProgress, singleRun, action },
    { dispatch, getState, extra, rejectWithValue },
  ) => {
    let blocked = false

    const validate = async () => {
      if (action !== 'open' && needValidation) {
        const validateResult = await extra.api.opday.validate()

        if (!validateResult.success) {
          dispatch(LKModalActions.openError(translate(validateResult.error.message)))
          return rejectWithValue('Failed to validate')
        }

        const { ErrorLevel } = validateResult.data

        if (ErrorLevel !== 100) {
          onValidate({
            data: validateResult,
            isError: true,
          })
          return rejectWithValue('Received validate error')
        }

        onValidate({
          data: validateResult,
          isError: false,
        })
      }

      return true
    }

    try {
      const kickAllUserResult = await dispatch(kickAllUser())

      if (kickAllUser.rejected.match(kickAllUserResult as any)) {
        return rejectWithValue('Failed to kick all users')
      }

      const blockResult = await extra.api.opday.block()

      if (!blockResult.success) {
        dispatch(LKModalActions.openError(translate(blockResult.error.message)))
        return rejectWithValue('Failed to block user')
      }

      blocked = true

      const validateResult = await validate()

      if (validateResult !== true) {
        return validateResult
      }

      const opdaySlice = getOpDay(getState())
      let currentDay = getDateInstance(opdaySlice.data.Open_Day)
      const openDay = getDateInstance(opdaySlice.data.Open_Day)

      const { perform, shouldPerform, onSuccess } = singleRun
        ? getSinglePerformOptions({ action, extra })
        : getPerformOptions({
            accountOnly,
            extra,
            goToDate,
            onProgress,
            openDay,
          })

      while (shouldPerform({ currentDay })) {
        const actionResult = await perform()

        if (!actionResult.success) {
          dispatch(LKModalActions.openError(translate(actionResult.error.message)))
          return rejectWithValue('Failed to open/close day')
        }

        while (true) {
          const statusResult = await extra.api.opday.getStatus(actionResult.data)

          if (!statusResult.success) {
            dispatch(LKModalActions.openError(translate(statusResult.error.message)))
            return rejectWithValue('Failed to get operation day status')
          }

          if (statusResult.data.StatusID === 4) {
            const getOpDayResult: any = await dispatch(loadOpDay())

            if (loadOpDay.rejected.match(getOpDayResult)) {
              return rejectWithValue('Failed to reload opday')
            }

            if (loadOpDay.fulfilled.match(getOpDayResult)) {
              currentDay = getDateInstance(getOpDayResult.payload.Open_Day)
              onSuccess({ currentDay })
            }

            // not validating second time for single run iteration or last iteration
            if (!singleRun && shouldPerform({ currentDay })) {
              const validateResult = await validate()

              if (validateResult !== true) {
                return validateResult
              }
            }

            break
          }

          if (statusResult.data.StatusID !== 2) {
            dispatch(LKModalActions.openError(translate('HINT_ERR_TITLE')))
            return rejectWithValue('Failed to finish proceed to day')
          }
        }
      }
    } finally {
      if (blocked) {
        const unblockResult = await extra.api.opday.unblock()

        if (!unblockResult.success) {
          dispatch(LKModalActions.openError(translate(unblockResult.error.message)))
          return rejectWithValue('Failed to unblock user')
        }
      }
    }
  },
)

interface LockOperationDayAttributes {
  lockDate: string
}

export const lockOperationDay = createAsyncThunk<void, LockOperationDayAttributes, ThunkApi>(
  '[OP_DAY] LOCK_OPERATION_DAY',
  async ({ lockDate }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.opday.lock(lockDate)

    if (!result.success) {
      dispatch(LKModalActions.openError(translate(result.error.message)))
      return rejectWithValue('Failed to lock operation day')
    }
  },
)

interface ValidateOperationDayAttributes {
  validationResult?: any
}

export const validateOperationDay = createAsyncThunk<any, ValidateOperationDayAttributes, ThunkApi>(
  '[OP_DAY] VALIDATE_OPERATION_DAY',
  async ({ validationResult }, { dispatch, extra, rejectWithValue }) => {
    let WHLSessionID: number,
      HashSum_WHLSessionID_: string,
      result = validationResult

    if (!result) {
      result = await extra.api.opday.validate()
    }

    if (!result.success) {
      dispatch(LKModalActions.openError(translate(result.error.message)))
      return rejectWithValue('Failed to validate operation day')
    }

    WHLSessionID = result.data.WHLSessionID
    HashSum_WHLSessionID_ = result.data.HashSum_WHLSessionID_

    const validationViewResult = await extra.api.opday.validateViewResult(WHLSessionID, HashSum_WHLSessionID_)

    if (!validationViewResult.success) {
      dispatch(LKModalActions.openError(translate(validationViewResult.error.message)))
      return rejectWithValue('Failed to validate view result')
    }

    return validationViewResult.data
  },
)
